<svelte:options strictprops={false} />

<script>
  import { getContext } from "svelte";
  import { bucketArray } from "overline";
  import { Link } from "svelte-routing";
  import upperFirst from "lodash/upperFirst";
  import { LinearDisplayFormat } from "dimtext";
  import CaretRightIcon from "@local/assets/icons/caret-right.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import CaretDownRight from "@local/assets/icons/caret-down-right.svg";
  import Viewport from "#lib/drawing/ViewportFixed.svelte";
  import typeDrawing from "@local/extensions/drawing/type-drawing.js";
  import ExitIcon from "@local/assets/icons/exit.svg";
  import FlagIcon from "@local/assets/icons/flag-filled.svg";
  import getTotalThickness from "@local/extensions/formatters/get-total-thickness.js";
  import { typeSurfaces } from "@local/lamina-core";
  import liteDrawing from "@local/extensions/drawing/lite-drawing-mini.js";
  import liteDrawingLg from "@local/extensions/drawing/lite-drawing.js";
  import PaperclipIcon from "@local/assets/icons/paperclip-xs.svg";
  import imageUrl from "#src/extensions/image-url.js";
  import dimSettings from "@local/extensions/utilities/dim-settings.js";
  import { isLocationVisible } from "#src/extensions/filter-items.js";
  import { drawingSrc } from "#src/api";

  export let group;
  export let report;
  export let section;
  export let group_by;
  export let columns;
  export let selected;
  export let typeIndex;
  export let attachments;
  export let linkPrefix;
  export let index;

  const typeColors = getContext("typeColors");

  // three-state boolean: 0 = collapsed, 1 = expanded, -1 = partially expanded
  let expanded = report?.default_type_view ?? 1;

  $: du = $group.data.settings.display_unit;
  $: ds = dimSettings($group.data.settings);
  $: displayUnit = ds.displayUnit === "millimeters" ? "mm" : "in";
  $: typeSettings = {
    display_unit: $group.data.settings.display_unit,
    mm_precision: $group.data.settings.mm_precision,
    decimal_precision: 3,
    fractional_precision: 5,
    dimension_format: $group.data.settings.dimension_format,
  };
  $: thickness = group_by === "type_id" && section.record && getTotalThickness(section.record, typeSettings);
  $: surfaces = group_by === "type_id" && section.record && typeSurfaces(section.record);
  $: typeAreaBare =
    du === "millimeters" ? (section.item_area * 0.092903).toFixed(2) : section.item_area.toFixed(2);
  $: areaFormatted = du === "millimeters" ? `${typeAreaBare} m²` : `${typeAreaBare} sf`;
  // $: undefinedItems = section.undefined_items || 0;
  $: noteLocations = $group.locations.order
    .filter((locId) => {
      const loc = $group.locations[locId];
      return loc?.record_type === "document_note";
    })
    .map((locId) => $group.locations[locId]);
  $: locationsByRecord = bucketArray(noteLocations, "record_id");

  function toggleExpanded() {
    if (!expanded) {
      expanded = -1;
    } else if (expanded > 0) {
      expanded = 0;
    } else {
      expanded = 1;
    }
  }

  function defaultFilters() {
    return {
      show_items: true,
      show_collections: true,
      show_types: true,
      show_notes: true,
      show_dimensions: true,
      location_filter: null,
      item_filter: null,
      collection_filter: null,
      type_filter: null,
      note_filter: null,
      hidden_pages: {},
      hidden_locations: {},
    };
  }

  function typeLink(report, id) {
    if (report?.link_destination) {
      return `${linkPrefix}/documents/${report.link_destination}?selected-records=${id}`;
    } else {
      return `${linkPrefix}/types/${id}`;
    }
  }

  function openingLink(report, id) {
    if (report?.link_destination) {
      return `${linkPrefix}/documents/${report.link_destination}?selected-records=${id}`;
    } else {
      return `${linkPrefix}/openings/${id}`;
    }
  }

  function itemLink(report, id) {
    if (report?.link_destination) {
      return `${linkPrefix}/documents/${report.link_destination}?selected-records=${id}`;
    } else {
      return `${linkPrefix}/items/${id}`;
    }
  }

  function noteLink(report, id) {
    if (report?.link_destination) {
      return `${linkPrefix}/documents/${report.link_destination}?selected-records=${id}`;
    } else {
      const locs = locationsByRecord[id] || [];

      // Find the first virtual document with this note visible;
      // If the note is not found on any virtual document, find the first imported document with this note.
      const virtualDoc = $group.documents.order.find((docId) => {
        const doc = $group.documents[docId];
        if (doc?.type !== "virtual") return false;
        const l = locs.filter((l) => l.document_id === doc.source_document_id);
        const filters = doc.data.filters || defaultFilters();
        return l.some((loc) => isLocationVisible(loc, $group, filters));
      });

      if (virtualDoc) {
        return `${linkPrefix}/documents/${virtualDoc}?selected-records=${id}`;
      } else {
        const importedDoc = $group.documents.order.find((docId) => {
          const doc = $group.documents[docId];
          if (doc?.type !== "imported") return false;
          const l = locs.filter((l) => l.document_id === docId);
          return l.length > 0;
        });

        if (importedDoc) {
          return `${linkPrefix}/documents/${importedDoc}?selected-records=${id}`;
        }
      }

      return null;
    }
  }

  function formatThickness(val) {
    const sdf = "FRACTIONAL";

    if (!val) return "Thickness TBD";

    const v = val.format(LinearDisplayFormat[sdf], sdf === "DECIMAL" ? 3 : 5, {
      displayUnit: "inches",
    });

    return `${v}"`;
  }

  function formatDim(val, ds) {
    if (!val) return "TBD";

    const { displayUnit, dimFormat, dimPrecision } = ds;

    return val.format(LinearDisplayFormat[dimFormat], dimPrecision, { displayUnit });
  }

  function undefinedItemText(u) {
    if (!u) return "";
    if (u === 1) return " (1 undefined item)";
    return ` (${u} undefined items)`;
  }
</script>

{#if section.type !== "notes" && section.type !== "linear"}
  <tbody class="report-section-header">
    <tr>
      <td class="align-top w-8">
        <button class="p-1" on:click={toggleExpanded}>
          {#if !expanded}
            <CaretRightIcon />
          {:else if expanded > 0}
            <CaretDownIcon />
          {:else}
            <CaretDownRight />
          {/if}
        </button>
      </td>
      <td colspan={columns.length + 2} class="align-top">
        <div>
          {#if section.record}
            {@const cat = section.record}
            <div class="text-sm flex justify-between">
              <div class="font-bold">
                {#if cat.mark && cat.name}
                  {cat.mark} - {cat.name}
                {:else if cat.mark && cat.description}
                  {cat.mark} - {cat.description}
                {:else}
                  {cat.mark}
                {/if}
              </div>
              {#if expanded <= 0}
                <div class="text-gray-600 font-bold">
                  {#if report?.category_callout === "area"}
                    {areaFormatted}
                  {:else if report?.category_callout === "quantity" && section.quantity != undefined}
                    Qty: {section.quantity}
                  {:else if report?.category_callout === "units"}
                    {section.units} Units
                  {/if}
                </div>
              {/if}
            </div>
          {:else}
            <div class="font-bold text-sm">{group_by === "collection_id" ? "Not in opening" : "No type"}</div>
          {/if}
          {#if expanded}
            <div class="flex gap-4 py-2">
              <div class="w-24 h-24 relative">
                {#if group_by === "type_id" && section.record}
                  {@const type = section.record}
                  <Link to={typeLink(report, type.id)}>
                    {#if type.drawing}
                      <div class="h-24 w-24 relative overflow-hidden flex justify-center items-center">
                        <img
                          class="m-auto max-w-full max-h-full border border-gray-400 object-contain text-xs text-gray-400"
                          alt={type.mark}
                          src={drawingSrc(type.drawing)} />
                      </div>
                    {:else if !type.data.layers && type.image}
                      <img
                        src={imageUrl(type.image)}
                        alt={type.name}
                        class="text-xs w-full h-full object-cover" />
                    {:else}
                      <Viewport
                        padding={1}
                        drawing={typeDrawing(type)}
                        cacheKey={type.id}
                        width={96}
                        height={96} />
                    {/if}
                  </Link>
                {:else if group_by === "collection_id" && section.record}
                  {@const type = section.record}
                  <Link to={openingLink(report, type.id)}>
                    {#if type.drawing}
                      <div class="h-24 w-24 relative overflow-hidden flex justify-center items-center">
                        <img
                          class="m-auto max-w-full max-h-full border border-gray-400 object-contain text-xs text-gray-400"
                          alt={type.mark}
                          src={drawingSrc(type.drawing)} />
                      </div>
                    {:else}
                      <Viewport
                        padding={1}
                        drawing={liteDrawingLg(type, { isCollection: true, showMark: false })}
                        cacheKey={type.id}
                        width={96}
                        height={96} />
                    {/if}
                  </Link>
                {:else}
                  <div class="w-full h-full border-gray-400 border-dashed" />
                {/if}
                {#if attachments?.[section.record?.id]}
                  <div class="absolute top-0 right-0 pointer-events-none">
                    <PaperclipIcon />
                  </div>
                {/if}
              </div>
              <div class="type-info">
                <div class="label">
                  {#if section.record?.data.layers}
                    {@const type = section.record}
                    {#if thickness}
                      <div>Thickness</div>
                    {/if}
                    {#each type.data.layers as layer, i}
                      <div>
                        {#if i === 0}Makeup{:else}&nbsp;{/if}
                      </div>
                    {/each}
                    <!-- {#if report?.show_total_area}
                    <div>Area</div>
                  {/if} -->
                  {/if}
                  {#if group_by === "collection_id"}
                    <div>Width</div>
                    <div>Height</div>
                    <!-- {#if report?.show_total_area}
                    <div>Area</div>
                  {/if} -->
                    {#if report?.show_total_categories}
                      <div>Quantity</div>
                    {/if}
                    <!-- {#if report?.show_total_units}
                    <div>Units</div>
                  {/if} -->
                  {:else}
                    <!-- <div>Units</div> -->
                  {/if}
                </div>
                <div>
                  {#if section.record?.data.layers}
                    {@const type = section.record}
                    {#if thickness}
                      <div>{thickness}</div>
                    {/if}
                    {#each type.data.layers as layer, i}
                      <div>
                        <span>
                          {upperFirst(layer.type)}:&nbsp;
                        </span>
                        <span>
                          {formatThickness(layer.material?.data?.thickness)}
                        </span>
                        {#if layer.material?.name}
                          <span>, {layer.material.name}</span>
                        {/if}
                        {#if layer.outboard_surface?.name}
                          <span>, #{surfaces[i]} {layer.outboard_surface.name}</span>
                        {/if}
                        {#if layer.inboard_surface?.name}
                          <span>, #{surfaces[i] + 1} {layer.inboard_surface.name}</span>
                        {/if}
                      </div>
                    {/each}
                    <!-- {#if report?.show_total_area}
                    <div>{areaFormatted}{undefinedItemText(undefinedItems)}</div>
                  {/if} -->
                  {/if}
                  {#if group_by === "collection_id"}
                    {@const coll = section.record}
                    <div>{coll?.width ? formatDim(coll.width, ds) + displayUnit : "TBD"}</div>
                    <div>{coll?.height ? formatDim(coll.height, ds) + displayUnit : "TBD"}</div>
                    <!-- {#if report?.show_total_area}
                    <div>{areaFormatted}{undefinedItemText(undefinedItems)}</div>
                  {/if} -->
                    {#if report?.show_total_categories}
                      <div>{section?.quantity ?? "N/A"}</div>
                    {/if}
                    <!-- {#if report?.show_total_units}
                    <div>{section.units}</div>
                  {/if} -->
                  {:else}
                    <!-- <div>{section.units}</div> -->
                  {/if}
                </div>
              </div>
            </div>
          {/if}
        </div>
      </td>
    </tr>
  </tbody>
{/if}
{#if expanded > 0 || section.type === "notes" || section.type === "linear"}
  <tbody class="report-section-rows">
    {#if section.rows.length}
      <tr>
        {#if section.type !== "notes" && section.type !== "linear"}
          <td />
        {/if}
        <td />
        <td class="border-b" />
        {#each columns as column, col}
          <th
            class="border-b px-1"
            data-column={col}
            data-type-index={typeIndex}
            class:w-7={column.prop === "approval_status"}>{column.label}</th>
        {/each}
        <th />
      </tr>
      {#each section.rows as row, i}
        {@const record = section.type === "notes" ? $group.document_notes[row.id] : $group.items[row.item_id]}
        {#if section.type === "linear"}
          {@const rowIndex = index + i}
          <tr>
            <td />
            <th
              class="border-l border-r border-b text-center w-8 bg-gray-100 font-normal"
              data-type-index={typeIndex}
              data-row={rowIndex}
              data-type-row={i}>{i + 1}</th>
            {#each columns as column, col}
              {@const td = row[column.prop]}
              <td
                class="border-b border-r px-1 whitespace-nowrap"
                class:bg-blue-100={selected[`${rowIndex}.${col}`]}
                class:text-right={typeof td === "number"}
                class:truncate={column.truncate}
                data-row={rowIndex}
                data-type-row={i}
                data-type-index={typeIndex}
                data-column={col}>
                {#if typeof td === "number"}
                  {#if displayUnit === "millimeters"}
                    {(td * 25.4).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                  {:else}
                    {td.toLocaleString("en-US", { maximumFractionDigits: 2 })}
                  {/if}
                {:else}
                  {td}
                {/if}
              </td>
            {/each}
            <td> </td>
          </tr>
        {:else if record}
          {@const rowIndex = index + i}
          {@const typeColor = $typeColors[record?.type_id]}
          <tr>
            {#if section.type !== "notes"}
              <td />
            {/if}
            {#if section.type === "notes" || section.type === "linear"}
              {@const link = noteLink(report, record.id)}
              <td class="w-6 show-hover">
                <div class="w-4 h-4 relative">
                  {#if link}
                    <Link to={link}>
                      <ExitIcon />
                    </Link>
                  {/if}
                </div>
              </td>
            {:else}
              <td class="w-7">
                <div class="w-4 h-4 relative">
                  <Link to={itemLink(report, record.id)}>
                    <Viewport
                      padding={1}
                      drawing={liteDrawing(record, { typeColor })}
                      width={16}
                      height={16} />
                  </Link>
                </div>
              </td>
            {/if}
            <th
              class="border-l border-r border-b text-center w-8 bg-gray-100 font-normal"
              data-type-index={typeIndex}
              data-row={rowIndex}
              data-type-row={i}>{i + 1}</th>
            {#each columns as column, col}
              {@const td = row[column.prop]}
              <td
                class="border-b border-r px-1 whitespace-nowrap"
                class:bg-blue-100={selected[`${rowIndex}.${col}`]}
                class:truncate={column.truncate}
                data-row={rowIndex}
                data-type-row={i}
                data-type-index={typeIndex}
                data-column={col}>
                {#if column.prop === "is_custom_shape"}
                  {td ? "See drawing" : ""}
                {:else if column.prop === "approval_status"}
                  {#if td === "approved"}
                    <div class="text-green-500">
                      <FlagIcon />
                    </div>
                  {:else if td === "flagged"}
                    <div class="text-red-500">
                      <FlagIcon />
                    </div>
                  {/if}
                {:else if td?.html}
                  {@html td.html}
                {:else}
                  {td}
                {/if}
              </td>
            {/each}
            <td>
              {#if attachments?.[record.id]}
                <Link to={itemLink(report, record.id)}>
                  <div>
                    <PaperclipIcon />
                  </div>
                </Link>
              {/if}
            </td>
          </tr>
        {/if}
      {/each}
      <tr class="font-bold">
        {#if section.type !== "notes" && section.type !== "linear"}
          <td />
        {/if}
        <td />
        <td />
        {#each columns as column, col}
          {#if column.prop === "value"}
            <td class="px-1 text-right">
              {#if displayUnit === "millimeters"}
                {(section.total_value * 25.4).toLocaleString("en-US", { maximumFractionDigits: 0 })}
              {:else}
                {section.total_value.toLocaleString("en-US", { maximumFractionDigits: 2 })}
              {/if}
            </td>
          {:else if column.prop === "quantity"}
            <td class="px-1">{section.units}</td>
          {:else if column.prop === "cache.area"}
            <td class="px-1">{typeAreaBare}</td>
          {:else if column.prop === "cache.weight" && section.weight}
            <td class="px-1">{Math.round(section.weight)}</td>
          {:else}
            <td></td>
          {/if}
        {/each}
      </tr>
    {:else}
      <tr>
        <!-- <td /> -->
        <td />
        <td colspan={columns.length + 1}> No items </td>
      </tr>
    {/if}
    <tr>
      <td>&nbsp;</td>
    </tr>
  </tbody>
{/if}

<style lang="scss">
  .type-info {
    @apply flex gap-2;

    .label {
      @apply text-gray-400 text-right;
    }
  }

  td {
    @apply border-gray-300;
  }

  td.show-hover {
    @apply opacity-0;
  }

  tr:hover {
    td.show-hover {
      @apply opacity-100;
    }
  }
</style>
